<template>
  <div>
    <!-- Hero Section -->
    <section class="py-5 bg-white" style="border-bottom: 1px solid #E2E8F0;">
      <CContainer>
        <CRow class="justify-content-center">
          <CCol lg="10" xl="9" class="text-center">
            <h1 class="display-3 font-weight-bold marketing-heading mb-4" style="font-size: 3rem; line-height: 1.1;">
              Everything Malaysian SMEs Need to <span class="text-marketing-primary">Grow with Confidence</span>
            </h1>
            <p class="lead marketing-body mb-4" style="font-size: 1.25rem; max-width: 800px; margin-left: auto; margin-right: auto;">
              From automated payroll to financial reporting, Niagaku provides all the tools you need to manage your business efficiently while staying 100% compliant with Malaysian regulations.
            </p>

            <!-- CTA Buttons -->
            <div class="d-flex flex-column flex-sm-row justify-content-center align-items-center mt-4" style="gap: 1rem;">
              <CButton
                class="btn-marketing-accent px-5 py-3 font-weight-600 btn-scale"
                size="lg"
                to="/signup"
              >
                Start Free 30-Day Trial
              </CButton>
              <CButton
                size="lg"
                class="px-5 py-3 font-weight-600 btn-scale"
                to="/pricing"
                style="font-size: 1.125rem; border: 2px solid #334155; background: white; color: #334155;"
              >
                View Pricing
              </CButton>
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Core Features Grid -->
    <section class="py-5 bg-marketing-light">
      <CContainer>
        <div class="text-center mb-5">
          <h2 class="h1 font-weight-bold marketing-heading mb-3">
            Powerful Features for Modern Businesses
          </h2>
          <p class="lead marketing-body">
            Everything you need to run your business, all in one place.
          </p>
        </div>

        <CRow>
          <!-- Payroll Feature -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-check-circle" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Automated Payroll</h5>
                <p class="marketing-body mb-3">
                  Automatic EPF, SOCSO, EIS, and LHDN tax calculations. Generate payslips instantly with built-in compliance checks.
                </p>
                <router-link to="/features/payroll" class="text-marketing-accent font-weight-medium" style="text-decoration: none;">
                  Learn more →
                </router-link>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Invoicing Feature -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-description" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Professional Invoicing</h5>
                <p class="marketing-body mb-3">
                  Create stunning invoices and quotations in seconds. Track payments, send reminders, and get paid faster.
                </p>
                <router-link to="/features/invoicing" class="text-marketing-accent font-weight-medium" style="text-decoration: none;">
                  Learn more →
                </router-link>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Accounting Feature -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-chart" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Complete Accounting</h5>
                <p class="marketing-body mb-3">
                  Full double-entry accounting with chart of accounts, journal entries, and automated financial reports.
                </p>
                <router-link to="/features/accounting" class="text-marketing-accent font-weight-medium" style="text-decoration: none;">
                  Learn more →
                </router-link>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Expenses Feature -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-wallet" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Expense Tracking</h5>
                <p class="marketing-body mb-3">
                  Capture receipts, categorize expenses, and track spending. Perfect for tax deductions and budgeting.
                </p>
                <router-link to="/features/expenses" class="text-marketing-accent font-weight-medium" style="text-decoration: none;">
                  Learn more →
                </router-link>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Inventory Feature -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-storage" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Inventory Management</h5>
                <p class="marketing-body mb-3">
                  Track stock levels, manage suppliers, and automate reordering. Real-time inventory updates across all sales channels.
                </p>
                <router-link to="/features/inventory" class="text-marketing-accent font-weight-medium" style="text-decoration: none;">
                  Learn more →
                </router-link>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Multi-User Feature -->
          <CCol lg="4" md="6" class="mb-4">
            <CCard class="h-100 border-0 shadow-sm marketing-card-feature hover-lift" style="background: white;">
              <CCardBody class="p-4">
                <div class="mb-3">
                  <div class="marketing-icon-wrapper">
                    <CIcon name="cil-people" size="xl"/>
                  </div>
                </div>
                <h5 class="font-weight-bold marketing-subheading mb-3">Multi-User Collaboration</h5>
                <p class="marketing-body mb-3">
                  Add your team with role-based permissions. Collaborate seamlessly with your accountant and employees.
                </p>
                <router-link to="/features/multi-user" class="text-marketing-accent font-weight-medium" style="text-decoration: none;">
                  Learn more →
                </router-link>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Compliance Section -->
    <section class="py-5 bg-white">
      <CContainer>
        <CRow class="align-items-center">
          <CCol lg="6" class="mb-4 mb-lg-0">
            <h2 class="h1 font-weight-bold marketing-heading mb-4">
              Malaysian Compliance, Automated
            </h2>
            <p class="marketing-body mb-4">
              Stop worrying about staying compliant. Niagaku automatically keeps you up-to-date with the latest Malaysian regulations.
            </p>

            <ul class="list-unstyled">
              <li class="d-flex align-items-start mb-3">
                <CIcon name="cil-check-circle" class="text-marketing-accent mr-3 mt-1" size="lg"/>
                <div>
                  <strong class="marketing-subheading d-block mb-1">EPF, SOCSO & EIS Calculations</strong>
                  <span class="marketing-body">Automatic rate updates based on latest government guidelines</span>
                </div>
              </li>
              <li class="d-flex align-items-start mb-3">
                <CIcon name="cil-check-circle" class="text-marketing-accent mr-3 mt-1" size="lg"/>
                <div>
                  <strong class="marketing-subheading d-block mb-1">SST Reporting</strong>
                  <span class="marketing-body">Generate SST-03 reports ready for RMCD submission</span>
                </div>
              </li>
              <li class="d-flex align-items-start mb-3">
                <CIcon name="cil-check-circle" class="text-marketing-accent mr-3 mt-1" size="lg"/>
                <div>
                  <strong class="marketing-subheading d-block mb-1">LHDN Tax Calculations</strong>
                  <span class="marketing-body">Employee tax calculations using official PCB tables</span>
                </div>
              </li>
              <li class="d-flex align-items-start mb-3">
                <CIcon name="cil-check-circle" class="text-marketing-accent mr-3 mt-1" size="lg"/>
                <div>
                  <strong class="marketing-subheading d-block mb-1">Audit Trail</strong>
                  <span class="marketing-body">Complete transaction history for audits and tax filing</span>
                </div>
              </li>
            </ul>
          </CCol>

          <CCol lg="6">
            <div class="p-5 rounded" style="background: linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%); border: 2px solid #CBD5E1;">
              <h4 class="font-weight-bold marketing-subheading mb-4 text-center">Trusted By Malaysian Authorities</h4>
              <div class="d-flex flex-wrap justify-content-center align-items-center" style="gap: 2rem;">
                <div class="trust-badge">
                  <CIcon name="cil-shield-alt" class="mr-2 text-marketing-accent"/>
                  Bank Negara Compliant
                </div>
                <div class="trust-badge">
                  <CIcon name="cil-check-circle" class="mr-2 text-marketing-accent"/>
                  LHDN Approved
                </div>
                <div class="trust-badge">
                  <CIcon name="cil-lock-locked" class="mr-2 text-marketing-accent"/>
                  ISO 27001 Certified
                </div>
                <div class="trust-badge">
                  <CIcon name="cil-building" class="mr-2 text-marketing-accent"/>
                  SSM Registered
                </div>
              </div>
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Final CTA -->
    <section class="py-5 bg-marketing-dark text-white" style="background: linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%);">
      <CContainer class="text-center">
        <h2 class="display-4 font-weight-bold mb-4" style="color: white;">
          Ready to Transform Your Business?
        </h2>
        <p class="lead mb-5" style="color: rgba(255, 255, 255, 0.9); max-width: 700px; margin-left: auto; margin-right: auto;">
          Join 500+ Malaysian businesses using Niagaku. Start your free 30-day trial today - no credit card required.
        </p>
        <div class="d-flex flex-column flex-sm-row justify-content-center align-items-center" style="gap: 1rem;">
          <CButton
            class="btn-marketing-accent mr-sm-3 mb-3 mb-sm-0 px-5 py-3 font-weight-600 btn-scale"
            size="lg"
            to="/signup"
          >
            Start Free 30-Day Trial
          </CButton>
          <CButton
            class="btn-outline-light-custom mr-sm-3 mb-3 mb-sm-0 px-5 py-3 btn-scale"
            size="lg"
            to="/features"
          >
            View Pricing
          </CButton>
        </div>
      </CContainer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Features',
  metaInfo: {
    title: 'Features - Niagaku Accounting & Payroll Software',
    meta: [
      {
        name: 'description',
        content: 'Discover all the features of Niagaku - automated payroll, invoicing, accounting, inventory, and more. Built specifically for Malaysian SMEs with full compliance.'
      }
    ]
  }
}
</script>

<style scoped>
/* Features page uses marketing CSS classes */
</style>
